import {
    ApprovalScopeEnum,
    ApprovalStatusEnum,
    BillingPeriodEnum,
    CommentItem,
    Company,
    CostCenter,
    CreditorInterface,
    CurrencyEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpenseReminder,
    ExpenseSplit,
    ExpenseStatusEnum,
    ExportFormatEnum,
    ExtendedReferencedUser,
    ExtendedReferencedVendor,
    FileUploadDuplicateData,
    FinApiPayment,
    FinApiPaymentStatusEnum,
    Hospitality,
    InvoiceInterface,
    InvoiceSplit,
    ItemSplit,
    Mileage,
    PerDiem,
    PerDiemDestination,
    PriceIntervalEnum,
    RightEnum,
    Role,
    SplitTypeEnum,
    SubscriptionTypeEnum,
    TabTypeEnum,
    Tax,
    Transaction,
    TransactionTypeEnum,
    TripFolder,
    User,
    UserRule,
    Vendor,
    VendorTypeEnum,
    WeavrAuthorizationTypesEnum,
    WeavrSettlementTypesEnum,
} from "@finway-group/shared/lib/models"
import { AutoGeneratedCommentsEnum } from "@finway-group/shared/lib/models/comment/autoGeneratedComments.enum"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { getApprovalProcess, roundNumberTo2Decimals } from "@finway-group/shared/lib/utils"
import { getApproverIdsOfApprovalProcesses } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { isBuCodeDATEVCompliant } from "@finway-group/shared/lib/utils/datevValidation"
import { isDiscountExpired } from "@finway-group/shared/lib/utils/expense.discount.utils"
import { calculateNetPriceFromGrossPriceAndTaxRate } from "@finway-group/shared/lib/utils/expense.utils"
import { flattenExpression } from "@finway-group/shared/lib/utils/logicalExpression.utils"
import { Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import dinero from "dinero.js"
import httpStatus from "http-status"
import { TFunction } from "i18next"
import moment from "moment"
import { RouterChildContext } from "react-router-dom"

import { FINWAY_ADMIN_EMAIL, NO_TAX_RATE } from "Shared/config/consts"
import { useActiveTab } from "Shared/hooks/expense.hooks"
import i18n from "Shared/locales/i18n"
import { AuthzService, BankService, CompanyService, CurrencyService, ExpenseService, FileService, NotificationService, UserService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationDurationSec, NotificationTypeEnum } from "Shared/services/notification.service"
import store from "Shared/store"
import {
    approveExpense,
    approveFolder,
    deleteExpense,
    detachOneExpense,
    rejectExpense,
    reviewExpense,
    reviewMultipleExpenses,
    sendReminder,
    setAllExpensesAsDone,
    setExpenseDone,
    setExpensePaid,
    setMultipleExpensesAsDoneByExpenseIds,
    setMultipleExpensesAsPaid,
    submitFolder,
    updateExpense,
} from "Shared/store/actions/expense/expenseActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { getNextApprover, getNextApproverId } from "Shared/utils/approvalProcess.utils"
import { copyDeeply, getUniqueElements, isEmptyObject, isExtendedAlphanumeric, isNotSet, isRightGranted } from "Shared/utils/helpers"

import { NOT_SET_VALUE } from "./expenseDetails/expenseSplitCells/cell.utils"
import { getFolderProgressionInfo } from "./folderUtils"

export const sortExpenses = (expenses: Array<Expense>, type: "ASC" | "DESC" = "DESC") =>
    expenses.sort((expA, expB) => {
        if (type === "ASC") {
            return new Date(expA.updatedAt).getTime() - new Date(expB.updatedAt).getTime()
        }

        return new Date(expB.updatedAt).getTime() - new Date(expA.updatedAt).getTime()
    })

export const needsDocs = (expense: Expense) => expense.status === ExpenseStatusEnum.DOCS_NEEDED

export const isMyPendingExpense = (userInfo: User, expense: Expense) => isPendingExpense(expense) && expense.requestedBy._id === userInfo.id

export const isPendingExpense = (expense: Expense) => expense.status === ExpenseStatusEnum.PURCHASE_PENDING || expense.status === ExpenseStatusEnum.INVOICE_PENDING

export const canApprove = (userInfo: User, expense: Expense) =>
    (expense.status === ExpenseStatusEnum.PURCHASE_PENDING || expense.status === ExpenseStatusEnum.INVOICE_PENDING) && expense.requestedBy._id !== userInfo.id

export const getPurchaseExpensesToApprove = (userInfo: User, expenses: Array<Expense>) =>
    getExpensesToApprove(userInfo, expenses).filter((exp) => exp.status === ExpenseStatusEnum.PURCHASE_PENDING)

export const getInvoiceExpensesToApprove = (userInfo: User, expenses: Array<Expense>) =>
    getExpensesToApprove(userInfo, expenses).filter((exp) => exp.status === ExpenseStatusEnum.INVOICE_PENDING)

export const getExpensesDocsNeeded = (expenses: Array<Expense>) => sortExpenses(expenses.filter((exp) => needsDocs(exp)))

export const getPendingExpenses = (userInfo: User, expenses: Array<Expense>) => sortExpenses(expenses.filter((exp) => isMyPendingExpense(userInfo, exp)))

export const getAllApprovedExpenses = (expenses: Array<Expense>) =>
    sortExpenses(expenses.filter((exp) => exp.status !== ExpenseStatusEnum.PURCHASE_PENDING && exp.status !== ExpenseStatusEnum.INVOICE_PENDING))

export const determinePossibleApproversForExpense = (
    expense: Expense,
    employees: Array<Employee>,
    costCenters: Array<CostCenter>,
    loggedInUser: Employee,
    isPreApprovedSubscription: boolean,
    filterRequester = true,
) => {
    const requestedBy = GetEmployeeById(employees, expense.requestedBy._id)
    const expensePrice = loggedInUser.settings.showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice

    // filter requester is by default set to true for expenses approval, and set to false for expense tagging list
    let filteredEmployees = employees.filter(
        (employee) =>
            !employee.activeCompanyProfile?.deleted &&
            (AuthzService.isPotentialApprover(employee.activeCompanyProfile.roleId) || (!filterRequester && employee.id === requestedBy?.id)),
    )

    // if not in the requester's limits and if the request's payment option is smart card, remove auto approver
    if (requestedBy) {
        // do allow it however if the card is already created
        const isAutoApproverAllowedForCardRequest = expense.card !== undefined

        const limit = getAutoApprovalLimit(employees, requestedBy, expense.currency)
        if (!limit || limit < expensePrice || (expense?.paymentOption === ExpensePaymentOptionEnum.SMART_CARD && !isAutoApproverAllowedForCardRequest)) {
            filteredEmployees = filteredEmployees.filter(({ email }) => email !== FINWAY_ADMIN_EMAIL)
        }
    }

    // also add the superior to the list
    if (requestedBy?.activeCompanyProfile.superior) {
        const superior = GetEmployeeById(employees, requestedBy.activeCompanyProfile.superior)
        if (superior && !superior.activeCompanyProfile.deleted && !filteredEmployees.find((e) => e.id === superior.id)) filteredEmployees.push(superior)
    }

    // for preapproved, also add the CC responsible to the list
    const costCenter = costCenters.find((costCenter: CostCenter) => costCenter._id === expense.costCenter)
    const isResponsibleUserInTheList = filteredEmployees.find((employee) => employee.id === costCenter?.responsibleUser)
    if (costCenter && !isResponsibleUserInTheList) {
        const responsibleUser = filteredEmployees.find((employee) => employee.id === costCenter.responsibleUser)
        if (responsibleUser && !responsibleUser?.activeCompanyProfile.deleted) filteredEmployees.push(responsibleUser)
    }

    // requester should not be a possible approver himself
    if (requestedBy && filterRequester) filteredEmployees = filteredEmployees.filter((employee) => employee?.id !== requestedBy.id)

    // lastly remove all that are listed in the limits and we already exceeded their approval limit
    filteredEmployees = filteredEmployees.filter((employee: Employee) => {
        if (!employee) return false
        if (AuthzService.isGlobalApprover(employee.activeCompanyProfile.roleId) && employee.email !== FINWAY_ADMIN_EMAIL) return true

        const foundInApproval = requestedBy?.activeCompanyProfile.approvals?.find((approval) => approval.approver === employee.id)
        if (foundInApproval) {
            const limit =
                foundInApproval.currency === expense.currency ? foundInApproval.limit : CurrencyService.convert(foundInApproval.limit, foundInApproval.currency, expense.currency)
            return limit >= expensePrice
        }
        return true
    })

    // This will make sure that the next approver of current approval process is shown in the dropdown.
    if (expense.approvalProcesses.length) {
        const nextApproverId = getNextApproverId(expense.approvalProcesses)
        if (nextApproverId) {
            const workflowApprover = GetEmployeeById(employees, nextApproverId)
            if (workflowApprover && !workflowApprover.activeCompanyProfile.deleted && !filteredEmployees.find((e) => e.id === workflowApprover.id)) {
                filteredEmployees.push(workflowApprover)
            }
        }
    }

    // if pre-approved sub and card doesn't have to be created, enable auto-approver
    if (
        isPreApprovedSubscription &&
        AuthzService.isLoggedInUserGlobalApprover() &&
        !filteredEmployees.find(({ email }) => email === FINWAY_ADMIN_EMAIL) &&
        (expense.card || expense.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD)
    ) {
        const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)
        autoApprover && filteredEmployees.push(autoApprover)
    }

    return filteredEmployees
}

const getAutoApprovalLimit = (employees: Array<Employee>, requestedBy: Employee, expenseCurrency: CurrencyEnum) => {
    const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)
    const autoApprovalLimit = requestedBy?.activeCompanyProfile.approvals?.find(({ approver }) => approver === autoApprover?.id)
    // prettier-ignore
    const limitValue = autoApprovalLimit && (autoApprovalLimit.currency === expenseCurrency ? autoApprovalLimit.limit : CurrencyService.convert(autoApprovalLimit.limit, autoApprovalLimit.currency, expenseCurrency));

    return limitValue
}

const confirmAdminNotOwnApproval = async (expense: Expense, scope: ApprovalScopeEnum, user: Employee, approverName: string) => {
    // if user is admin check if we need to show a confirmation modal if he/she is going to approve for someone else
    const approvalProcess = getApprovalProcess(scope, expense.approvalProcesses)
    const activeStep = approvalProcess?.steps.find(({ status }) => status === ApprovalStatusEnum.IN_PROGRESS)
    const stepApprovals = activeStep ? flattenExpression(activeStep.approvalExpression) : []
    const userPendingApprovals = stepApprovals.filter((approval) => !approval.approvedBy && !approval.rejectedBy && approval.approver._id === user.id)

    // If user still has own approvals pending, no need to ask for a confirm. He/she will approve his/her pending approval.
    if (userPendingApprovals.length) return true

    return DialogService.confirmAdminNotOwnApproval(approverName, expense.kind === ExpenseKindEnum.TRIP_FOLDER)
}

export const approveExpenseRequest = async (expense: Expense, scope: ApprovalScopeEnum, user: Employee, approverName?: string) => {
    if (AuthzService.isLoggedInUserAllowedToApproveExpense(expense) && approverName) {
        if (!(await confirmAdminNotOwnApproval(expense, scope, user, approverName))) return
    }

    return approveExpense(
        expense.id,
        scope,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.approved.title`), i18n.t(`notification:request.approved.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.approved.title`))
            throw err
        })
}

export const approveFolderRequest = async (folder: TripFolder, scope: ApprovalScopeEnum, user: Employee, approverName?: string) => {
    if (AuthzService.isLoggedInUserGlobalApprover() && approverName) {
        if (!(await confirmAdminNotOwnApproval(folder, scope, user, approverName))) return
    }

    return approveFolder(
        folder.id,
        scope,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.approved.title`), i18n.t(`notification:request.approved.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.approved.title`))
            throw err
        })
}

export const reviewFolder = async (folder: TripFolder, subExpenses: Array<Expense> = []) => {
    const { processableSubExpenses } = getFolderProgressionInfo(folder, subExpenses)

    if (processableSubExpenses.some((processableSubExpense) => processableSubExpense.invoiceNumber && !isExtendedAlphanumeric(processableSubExpense.invoiceNumber))) {
        try {
            await confirmDatevWarningPromise(true)
        } catch {
            return
        }
    }

    return reviewMultipleExpenses([folder.id, ...processableSubExpenses.map((e) => e.id)])(store.dispatch).catch((err) => {
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:error"))
    })
}

export const rejectExpenseRequest = ({
    expense,
    rejectionNote,
    history,
    scope,
}: {
    expense: Expense
    rejectionNote: string
    history: RouterChildContext["router"]["history"]
    scope?: ApprovalScopeEnum
}) =>
    rejectExpense(
        expense.id,
        rejectionNote,
        scope,
    )(store.dispatch)
        .then(() => {
            if (isFolderExpense(expense)) {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:trip_folder.rejected.title`),
                    i18n.t(`notification:trip_folder.rejected.message`),
                    NotificationDurationSec.REQUEST_CREATION,
                    undefined,
                    {
                        onClick: createLinkToRequestFunction(expense, history),
                        text: i18n.t("notification:trip_folder.rejected.link"),
                    },
                )
            } else {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:request.rejected.title`),
                    i18n.t(`notification:request.rejected.message`),
                    NotificationDurationSec.REQUEST_CREATION,
                    undefined,
                    {
                        onClick: createLinkToRequestFunction(expense, history),
                        text: i18n.t("notification:request.rejected.link"),
                    },
                )
            }
        })
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.rejected.title`))
            throw err
        })

export const cancelExpenseRequest = (expense: Expense, shouldUseDeleteLabel = false, onCancel?: () => void) => {
    const { confirm } = Modal
    const isTripFolder = expense.kind === ExpenseKindEnum.TRIP_FOLDER

    let confirmTranslations = {
        title: i18n.t("confirm:request.cancel.title"),
        content: i18n.t("confirm:request.cancel.message"),
        cancelText: i18n.t("confirm:request.cancel.cancel"),
        okText: i18n.t("confirm:request.cancel.confirm"),
    }

    if (shouldUseDeleteLabel) {
        confirmTranslations = {
            title: i18n.t("confirm:request.delete.title"),
            content: i18n.t("confirm:request.delete.message"),
            cancelText: i18n.t("confirm:request.delete.cancel"),
            okText: i18n.t("confirm:request.delete.confirm"),
        }

        if (isTripFolder) {
            confirmTranslations = {
                title: i18n.t("confirm:trip_folder.delete.title"),
                content: i18n.t("confirm:trip_folder.delete.message"),
                cancelText: i18n.t("confirm:trip_folder.delete.cancel"),
                okText: i18n.t("confirm:trip_folder.delete.confirm"),
            }
        }
    }

    const translationType = isTripFolder ? "trip_folder" : "request"

    confirm({
        ...confirmTranslations,
        type: "warning",
        onOk() {
            return new Promise<void>((resolve, reject) => {
                deleteExpense(expense.id)(store.dispatch)
                    .then(() => {
                        NotificationService.send(
                            NotificationTypeEnum.WARNING,
                            i18n.t(`notification:${translationType}.cancelled.title`),
                            i18n.t(`notification:${translationType}.cancelled.message`),
                        )
                        resolve()
                    })
                    .catch((err) => {
                        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:${translationType}.cancel.title`))
                        reject()
                    })
            })
                .then(() => onCancel && onCancel())
                .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:${translationType}.cancel.title`)))
        },
        onCancel() {},
    })
}

export const handleTripFolderSubmit = (folder: TripFolder) =>
    submitFolder(folder.id)(store.dispatch)
        .then(() => {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:trip_folder.submitted.title`), i18n.t(`notification:trip_folder.submitted.message`))
        })
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.rejected.title`))
            throw err
        })

export const markExpenseAsReviewed = async (expense: Expense, refetchExpenseTables = true) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    // prettier-ignore
    if (expense.paymentFlowInformation && PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation) && !(await DialogService.confirmPaymentFlowInformationAlreadyPaidWarningOnReview(expense.paymentFlowInformation))) return

    return reviewExpense(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed.title`), i18n.t(`notification:request.reviewed.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
            throw err
        })
}

export const payExpenses = (expenses: Array<Expense>, accountId: string) =>
    BankService.issuePayment(
        accountId,
        expenses.map((e: Expense) => e.id),
    )
        .then(() =>
            NotificationService.send(NotificationTypeEnum.INFO, i18n.t("notification:banking.payment_started.title"), i18n.t("notification:banking.payment_started.message"), 30),
        )
        .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:banking.error")))

export const markExpenseAsPaid = (expense: Expense, refetchExpenseTables = true) =>
    setExpensePaid(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid.title`), i18n.t(`notification:request.paid.message`)))
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
            throw err
        })

export const markExpenseAsDone = (expense: Expense, refetchExpenseTables = true) =>
    setExpenseDone(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => {
            if (isFolderExpense(expense)) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:trip_folder.exported.title`), i18n.t(`notification:trip_folder.exported.message`))
            } else {
                NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported.title`), i18n.t(`notification:request.exported.message`))
            }
        })
        .catch((err) => {
            if (err === "cancelled") return
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
            throw err
        })

export const markMultipleExpensesAsReviewed = async (expenses: Array<Expense>) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    // prettier-ignore
    const paymentFlowSet = expenses.find((expense) => expense.paymentFlowInformation && PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation))
    if (paymentFlowSet && !(await DialogService.confirmPaymentFlowsOnBulkReview())) return

    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await reviewMultipleExpenses(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.reviewed_bulk_partial.title`), i18n.t(`notification:request.reviewed_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }), 0)
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed_bulk.title`), i18n.t(`notification:request.reviewed_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
        throw err
    }
}

export const markMultipleExpensesAsPaid = async (expenses: Array<Expense>) => {
    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await setMultipleExpensesAsPaid(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.paid_bulk_partial.title`), i18n.t(`notification:request.paid_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }), 0)
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid_bulk.title`), i18n.t(`notification:request.paid_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
        throw err
    }
}

export const markMultipleExpensesAsDone = async (expensesArrayOrExpenseQuery: Array<Expense> | string) => {
    try {
        const filteredOutExpenseNumbers = await setMultipleExpensesAsDoneAndReturnFailures(expensesArrayOrExpenseQuery)

        if (filteredOutExpenseNumbers.length) {
            // prettier-ignore
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t(`notification:request.exported_bulk_partial.title`), i18n.t(`notification:request.exported_bulk_partial.message`, { expenses: filteredOutExpenseNumbers.join(", ") }), 0)
        } else {
            // TODO: check if this notification is ok w/ product
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported_bulk.title`), i18n.t(`notification:request.exported_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") return
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
        throw err
    }
}

const setMultipleExpensesAsDoneAndReturnFailures = async (expensesArrayOrExpenseQuery: Array<Expense> | string) => {
    if (Array.isArray(expensesArrayOrExpenseQuery)) {
        const expenseIds = expensesArrayOrExpenseQuery.map(({ id }) => id)
        return setMultipleExpensesAsDoneByExpenseIds(expenseIds)(store.dispatch)
    }

    return setAllExpensesAsDone(expensesArrayOrExpenseQuery)(store.dispatch)
}

export const getRejectionComment = (expense: Expense, defaultKindMessage: AutoGeneratedCommentsEnum): AutoGeneratedCommentsEnum => {
    if (expense.isReimbursement) {
        switch (expense.kind) {
            case ExpenseKindEnum.HOSPITALITY:
                return AutoGeneratedCommentsEnum.HOSPITALITY_REJECTED
            case ExpenseKindEnum.PER_DIEM:
                return AutoGeneratedCommentsEnum.PER_DIEM_REJECTED
            case ExpenseKindEnum.MILEAGE:
                return AutoGeneratedCommentsEnum.MILEAGE_REJECTED
            default:
                return AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_REJECTED
        }
    } else {
        return defaultKindMessage
    }
}

export const getInvoiceApprovalComment = (expense: Expense): AutoGeneratedCommentsEnum => {
    if (expense.isReimbursement) {
        switch (expense.kind) {
            case ExpenseKindEnum.PER_DIEM:
                return AutoGeneratedCommentsEnum.PER_DIEM_APPROVED
            case ExpenseKindEnum.MILEAGE:
                return AutoGeneratedCommentsEnum.MILEAGE_APPROVED
            case ExpenseKindEnum.HOSPITALITY:
                return AutoGeneratedCommentsEnum.HOSPITALITY_APPROVED
            default:
                return AutoGeneratedCommentsEnum.STANDARD_REIMBURSEMENT_APPROVED
        }
    } else {
        return AutoGeneratedCommentsEnum.INVOICE_REQUEST_APPROVED
    }
}

export const restoreExpense = (expensePara: Expense, currentUserId: string) => {
    const expense = copyDeeply(expensePara, Expense)
    expense.deleted = false
    expense.dateDeleted = undefined

    return applyExpenseUpdate(expense, "restored")
}

export const detachExpense = (expensePara: Expense) => {
    const expense = copyDeeply(expensePara, Expense)
    return detachOneExpense(expense.id)(store.dispatch)
}

export const applyExpenseUpdate = (expense: Expense, notificationLabel: string, skipHandling = false, refetchExpenseTables = true) =>
    updateExpense(
        expense.id,
        expense,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => {
            if (!skipHandling)
                NotificationService.send(
                    NotificationTypeEnum.SUCCESS,
                    i18n.t(`notification:request.${notificationLabel}.title`),
                    i18n.t(`notification:request.${notificationLabel}.message`),
                )
        })
        .catch((err) => {
            if (err === "cancelled") return
            if (!skipHandling) NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.${notificationLabel}.title`))
            throw err
        })

export const sortDesc = (expenses: Array<Expense>, by: string = "dateLastUpdated") =>
    expenses.sort((expA: { [key: string]: any }, expB: { [key: string]: any }) => new Date(expB[by]).getTime() - new Date(expA[by]).getTime())

export const isExpenseProcessed = (expense: Expense) => expense.status === ExpenseStatusEnum.APPROVED || expense.status === ExpenseStatusEnum.DECLINED

export const isExpenseStatusAtLeastApproved = (status: ExpenseStatusEnum) =>
    [ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(status)

export const isExpenseStatusAtLeastDocsNeeded = (status: ExpenseStatusEnum) => status !== ExpenseStatusEnum.PURCHASE_PENDING

export const getBillingInMonths = (billingInterval: BillingPeriodEnum) =>
    billingInterval === BillingPeriodEnum.MONTHLY ? 1 : billingInterval === BillingPeriodEnum.QUARTERLY ? 3 : 12

export const getPriceIntervalForBillingPeriod = (billingPeriod: number) => {
    switch (billingPeriod) {
        case BillingPeriodEnum.QUARTERLY:
            return PriceIntervalEnum.QUARTERLY
        case BillingPeriodEnum.YEARLY:
            return PriceIntervalEnum.YEARLY
        default:
            return PriceIntervalEnum.MONTHLY
    }
}

export const sendEmailReminder = (userId: string, employees: Array<Employee>, expense: Expense) =>
    // send out email confirmation
    sendReminder(expense.id)(store.dispatch)
        .then(() => {
            if (expense.status === ExpenseStatusEnum.DOCS_NEEDED) {
                const requester = GetEmployeeById(employees, expense.requestedBy._id)
                if (!requester) {
                    NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                    return
                }
            } else {
                const approver = getNextApprover(expense.approvalProcesses)

                if (!approver) {
                    NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                    return
                }
            }
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t("notification:request.reminder_sent.title"), i18n.t("notification:request.reminder_sent.message"))
        })
        .catch((error: any) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.send_reminder.title"))
        })

export const getStatusLabel = (status: ExpenseStatusEnum, isDraft?: boolean) => {
    if (isDraft) {
        return i18n.t("label:draft")
    }
    switch (status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
            return i18n.t("label:purchase_pending")
        case ExpenseStatusEnum.DOCS_NEEDED:
            return i18n.t("label:documents_needed")
        case ExpenseStatusEnum.INVOICE_PENDING:
            return i18n.t("label:invoice_pending")
        case ExpenseStatusEnum.APPROVED:
            return i18n.t("label:in_review")
        case ExpenseStatusEnum.DECLINED:
            return i18n.t("label:declined")
        case ExpenseStatusEnum.REVIEWED:
            return i18n.t("label:in_payment")
        case ExpenseStatusEnum.PAID:
            return i18n.t("label:in_export")
        case ExpenseStatusEnum.DONE:
            return i18n.t("label:processed")
        default:
            return "n/a"
    }
}

export const getExpensesToApprove = (userInfo: User, expenses: Array<Expense>) => sortExpenses(expenses.filter((exp) => canApprove(userInfo, exp)))

export const isRequestActionDisabled = (user: Employee, vendors: Array<Vendor>, costCenters: Array<CostCenter>, employees: Array<Employee>, rolesMap: Map<string, Role>) => {
    const filteredEmployees = employees.filter((employeeToFilter) => {
        const role = rolesMap.get(employeeToFilter.activeCompanyProfile?.roleId)
        return (
            employeeToFilter.activeCompanyProfile &&
            !employeeToFilter.activeCompanyProfile.deleted &&
            ((isRightGranted(role?.rights, RightEnum.EXPENSE__ALL__APPROVE) && employeeToFilter.email !== FINWAY_ADMIN_EMAIL) ||
                (isRightGranted(role?.rights, RightEnum.EXPENSE__TEAM__APPROVE) && employeeToFilter.activeCompanyProfile.team === user.activeCompanyProfile.team))
        )
    })

    return vendors.length === 0 || costCenters.length === 0 || filteredEmployees.length === 0
}

// TODO: can we remove this after the migration?
export const convertToSortingString = (sortingCriteria: Array<any>) => {
    if (sortingCriteria.length === 0) return ""
    const sortingStrings = sortingCriteria.map((criteria: any, i) => {
        // antd understands "ascend and descend" and back end understands "asc and desc"
        let criteriaOrderAux = criteria.order
        if (criteria.order === "ascend") criteriaOrderAux = "asc"
        if (criteria.order === "descend") criteriaOrderAux = "desc"
        return `${criteriaOrderAux}(${criteria.field})`
    })
    return `&sortBy=${sortingStrings.join(",")}`
}

// antd understands "ascend and descend" and back end understands "asc and desc"
export const convertSortingToString = (sorting: any) => {
    if (sorting.order || sorting.field) return `&sortBy=${sorting.order === "ascend" ? "asc" : "desc"}(${sorting.field})`
    return ""
}

export const isSubscriptionRenewingSoon = (date: Date) => {
    const in8Weeks = moment().add(8, "w")
    const currentDate = moment()
    return moment(date).isBetween(currentDate, in8Weeks, undefined, "[]")
}

export const getSubscriptionPriceInterval = (expense: Expense) => {
    switch (expense.billingPeriod) {
        case BillingPeriodEnum.MONTHLY:
            return PriceIntervalEnum.MONTHLY
        case BillingPeriodEnum.QUARTERLY:
            return PriceIntervalEnum.QUARTERLY
        case BillingPeriodEnum.YEARLY:
            return PriceIntervalEnum.YEARLY
    }
}

export const confirmDatevWarning = (callback: any, multiple = false) => {
    Modal.confirm({
        title: i18n.t("confirm:request.datev_warning.title"),
        content: i18n.t(`confirm:request.datev_warning.message${multiple ? "_multiple" : ""}`),
        cancelText: i18n.t("confirm:request.datev_warning.cancel"),
        type: "warning",
        okText: i18n.t("confirm:request.datev_warning.confirm"),
        onOk() {
            callback()
        },
    })
}

export const confirmDatevWarningPromise = (multiple = false) =>
    new Promise<void>((resolve, reject) => {
        Modal.confirm({
            title: i18n.t("confirm:request.datev_warning.title"),
            content: i18n.t(`confirm:request.datev_warning.message${multiple ? "_multiple" : ""}`),
            cancelText: i18n.t("confirm:request.datev_warning.cancel"),
            type: "warning",
            okText: i18n.t("confirm:request.datev_warning.confirm"),
            onOk() {
                resolve()
            },
            onCancel() {
                reject()
            },
        })
    })

export const getExpenseCards = (expenses: Array<any>) =>
    expenses
        .map((exp) => exp.card)
        .filter((card) => card)
        .join(",")

/**
 * Only for expenses that have transactionState or expenses that have looked up/populated the matchedTransactions
 */
export const summarizeExpenseTransactions = (expense: Expense) => {
    const { transactionState } = expense
    const matchedTransactions = expense.matchedTransactions as Array<Transaction>

    if (!isNotSet(transactionState)) return transactionState

    if (matchedTransactions?.length > 0) {
        if ([TransactionTypeEnum.BANK_TRANSACTION, TransactionTypeEnum.CSV_TRANSACTION].includes(matchedTransactions[0].transactionType)) {
            return matchedTransactions[0].status
        }
        if (matchedTransactions[0].transactionType === TransactionTypeEnum.CARD_TRANSACTION) {
            const settlementTransactions = matchedTransactions.filter((mt) => Object.values(WeavrSettlementTypesEnum).includes(mt.cardData.cardTransactionType as any))

            // show pending when there's at least one pending
            if (matchedTransactions.some((mt) => mt.status === TransactionStatusEnum.PENDING)) return TransactionStatusEnum.PENDING

            // if 1 settlement succeeded, show completed
            if (settlementTransactions.some((mt) => mt.status === TransactionStatusEnum.COMPLETED)) return TransactionStatusEnum.COMPLETED

            // if there is none pending, no settlement and at least one failed that's not a reversal, show as failed
            if (matchedTransactions.some((mt) => mt.status === TransactionStatusEnum.FAILED && mt.cardData.cardTransactionType !== WeavrAuthorizationTypesEnum.ONLINE_REVERSE))
                return TransactionStatusEnum.FAILED
        }
    }
    return undefined
}

export const getPeriodPricesForSubscription = (expense: any, showGrossAmount: boolean) => {
    let monthlyPrice
    let quarterlyPrice
    let yearlyPrice
    switch (expense.billingPeriod) {
        case BillingPeriodEnum.MONTHLY:
            monthlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            quarterlyPrice = roundNumberTo2Decimals(monthlyPrice * 3)
            yearlyPrice = roundNumberTo2Decimals(monthlyPrice * 12)
            break
        case BillingPeriodEnum.QUARTERLY:
            quarterlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            monthlyPrice = roundNumberTo2Decimals(quarterlyPrice / 3)
            yearlyPrice = roundNumberTo2Decimals(quarterlyPrice * 4)
            break
        case BillingPeriodEnum.YEARLY:
            yearlyPrice = showGrossAmount ? expense.totalGrossPrice : expense.totalNetPrice
            monthlyPrice = roundNumberTo2Decimals(yearlyPrice / 12)
            quarterlyPrice = roundNumberTo2Decimals(yearlyPrice / 4)
    }
    return { monthlyPrice: Number(monthlyPrice), quarterlyPrice: Number(quarterlyPrice), yearlyPrice: Number(yearlyPrice) }
}

export const doTotalAndInlineAmountsMismatch = (expense: Expense, taxes: Array<Tax>, isNew: boolean = true) => {
    // early out
    if (expense.kind !== ExpenseKindEnum.ONE_TIME_EXPENSE || expense.splits.length === 0 || expense.splitType === SplitTypeEnum.SPLIT) return false

    const totalNetPriceDiff = isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.NET_PRICE)

    const totalGrossPriceDiff = isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.GROSS_PRICE)

    // prettier-ignore
    const taxRateDiff = expense.taxRate?._id === NO_TAX_RATE || (!isNew && !expense.taxRate)
        ? isExpenseAndTotalSplitPriceMatch(expense, PriceSourceEnum.TAX_PRICE)
        : getCommonTaxRateAcrossItems(expense.splits)?._id !== expense.taxRate?._id

    return totalNetPriceDiff || totalGrossPriceDiff || taxRateDiff
}

export enum PriceSourceEnum {
    NET_PRICE = "netPrice",
    TAX_PRICE = "taxPrice",
    GROSS_PRICE = "grossPrice",
}

export const isExpenseAndTotalSplitPriceMatch = (expense: Expense, priceSource: PriceSourceEnum) => {
    let expenseTotalPrice: number

    switch (priceSource) {
        case PriceSourceEnum.NET_PRICE:
            expenseTotalPrice = expense.totalNetPrice
            break
        case PriceSourceEnum.GROSS_PRICE:
            expenseTotalPrice = expense.totalGrossPrice
            break
        case PriceSourceEnum.TAX_PRICE:
            expenseTotalPrice = expense.totalTaxPrice
            break
    }

    return (
        roundNumberTo2Decimals(expenseTotalPrice) !==
        roundNumberTo2Decimals(expense.splits.reduce((total, curr: ExpenseSplit) => roundNumberTo2Decimals(total + curr[priceSource]), 0))
    )
}

export const syncTotalExpenseAmountsToItems = (splits: Array<ExpenseSplit>) => {
    // total net price = sum of all net prices of items (calculates in integer as float might have information loss)
    const totalNetPrice = toAmount(splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.netPrice)), toDinero(0)))

    // total gross price = sum of all gross prices of items (calculates in integer as float might have information loss)
    const totalGrossPrice = toAmount(splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.grossPrice)), toDinero(0)))

    // if there's only one common VAT across all items, then use that one as the global VAT otherwise, set  none
    const taxRate = getCommonTaxRateAcrossItems(splits)

    // calc total tax price and gross price by using the above
    // TODO: EXPENSE REFACTOR - double check accuracy
    // const totalTaxPrice = roundNumberTo2Decimals(taxRate !== NO_TAX_RATE ? (totalNetPrice * getTax(taxes, taxRate)) / 100 : calcSumOverAllItemSplitTaxes(expense, taxes))
    const totalTaxPrice = toAmount(splits.reduce((total, curr: ExpenseSplit) => total.add(toDinero(curr.taxPrice)), toDinero(0)))

    // clear up all float error in the splits.
    const newSplits = [...splits].map((split) => ({
        ...split,
        taxPrice: toAmount(toDinero(split.taxPrice)),
        grossPrice: toAmount(toDinero(split.grossPrice)),
        netPrice: toAmount(toDinero(split.netPrice)),
    }))

    return { totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate, splits: newSplits }
}

const getCommonTaxRateAcrossItems = (splits: Array<ExpenseSplit>) => {
    const splitTaxIds: Array<string> = splits.map((split) => split.taxRate?._id!)
    const hasEqualTaxRates = splitTaxIds.every((val) => val === splitTaxIds[0])
    const firstSplitTaxRate = splits[0]?.taxRate === undefined ? { _id: NO_TAX_RATE } : splits[0].taxRate
    return hasEqualTaxRates && splits.length > 0 ? firstSplitTaxRate : { _id: NO_TAX_RATE }
}

export const calculateTotalTax = (splits: Array<ExpenseSplit>) => {
    const taxTotal = splits.reduce((acc, split) => acc + split.taxPrice, 0)
    return roundNumberTo2Decimals(taxTotal)
}

// When cc/cc2/ea is different for each items, then expense shouldnt have any of these info.\
// But when it's all the same, then it should be set to follow what has been filled in the items.
export const getExpenseFieldsSummaryBasedOnSplits = (splits: Array<ExpenseSplit>, expenseTaxRate?: string, syncTax: boolean = false): Partial<ExpenseSplit> => {
    const isUniform = isSplitPropertiesSame(splits)

    // If tax is synced and tax are all the same, adjust the taxRate to be the split's taxRate.
    // If not, set to the expense's tax rate
    let targetTaxRate: { _id: string } | undefined = expenseTaxRate ? { _id: expenseTaxRate } : undefined
    if (isUniform.taxRate && syncTax && splits[0]?.taxRate?._id) {
        targetTaxRate = { _id: splits[0]?.taxRate?._id }
    } else if (syncTax) {
        targetTaxRate = undefined
    }

    return {
        costCenter: isUniform.costCenter ? splits[0]?.costCenter : undefined,
        costCenter2: isUniform.costCenter2 ? splits[0]?.costCenter2 : undefined,
        expenseAccount: isUniform.expenseAccount ? splits[0]?.expenseAccount : undefined,
        taxRate: targetTaxRate?._id === NO_TAX_RATE ? undefined : targetTaxRate,
    }
}

/**
 * Sets undefined taxRate into NO_TAX_RATE so its recognizable by antd form
 */
export const initializeExpenseItemSplitsForForm = (expenseData: any): Array<ExpenseSplit> => {
    if (expenseData.splits) {
        const clonedSplits = JSON.parse(JSON.stringify(expenseData.splits))
        for (const split of clonedSplits) {
            if (split.taxRate === undefined) split.taxRate = { _id: NO_TAX_RATE }
        }
        return clonedSplits
    }
    return []
}

export const deleteUnnecessaryExpenseFormData = (expenseData: any) => {
    if (expenseData.taxRate && expenseData.taxRate._id === NO_TAX_RATE) delete expenseData.taxRate
    if (expenseData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD || expenseData.paymentFlowInformation === NOT_SET_VALUE) delete expenseData.paymentFlowInformation
    if (expenseData.splits)
        expenseData.splits = expenseData.splits.map((itemSplit: ItemSplit) => ({
            ...itemSplit,
            taxRate: itemSplit.taxRate?._id === NO_TAX_RATE ? undefined : itemSplit.taxRate,
        }))
    if (expenseData.items) delete expenseData.items // TODO - EXPENSE REFACTOR: Remove this, when Expense deserializable is updated to get rid of items.
}

/**
 * if this returns true, that means the there's no duplicate, or the user clicked on "continue".
 * if this returns false, that means the there's a duplicate AND the user clicked on "back"
 */
export const checkForDuplicate = async (expense: Expense, invoiceNumber?: string, options?: { checkInvoice?: boolean; checkExpense?: boolean; ignoreIds?: Array<string> }) => {
    const data = await ExpenseService.checkForDuplicate({ id: expense.id, invoiceNumber: invoiceNumber || expense.invoiceNumber, ...(options || {}) })
    return !data.isDuplicate || (await checkForDuplicateInvoiceNumber(data))
}

export const checkForDuplicateInvoiceFile = async (conflictingData: FileUploadDuplicateData) =>
    !conflictingData.isDuplicate || (await DialogService.confirmDuplicateInvoiceFileWarning(conflictingData))

export const checkForDuplicateInvoiceNumber = async (conflictingData: any) =>
    !conflictingData.isDuplicate || (await DialogService.confirmDuplicateInvoiceNumberWarning(conflictingData))

interface ExpenseUpdateData extends Pick<Expense, "costCenter" | "costCenter2" | "expenseAccount" | "paymentFlowInformation"> {}

/**
 * Applies vendor and employee rules corrding to company, user and vendor settings depending on expense vendorType
 *
 * @returns expense data that should be updated
 */
export const buildUpdateDataAccordingToSetting = (expense: Expense, vendors: Array<Vendor>, users: Array<Employee>, company: Company) => {
    const userId = expense?.requestedBy._id
    const vendorId = expense?.vendor?._id
    const companyId = company._id
    const vendorRulesShouldOverwrite = company.vendorRulesOverwriteEmployeeRulesEnabled

    const updateData: ExpenseUpdateData = {}

    if (!userId && !vendorId) return updateData
    if (userId && !vendorId) return buildEmployeeRuleUpdateData(userId, users, companyId, expense.expenseAccount?._id)

    // if the VendorTypeEnum is USER => only apply the employee rules (of the person requesting the expense here: userId)
    if (expense.vendorType === VendorTypeEnum.USER) {
        const employeeRules = buildEmployeeRuleUpdateData(userId!, users, companyId, expense.expenseAccount?._id)

        if (isEmptyObject(employeeRules)) return updateData

        updateData.costCenter = employeeRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = employeeRules.costCenter2 ?? expense.costCenter2
        updateData.expenseAccount = employeeRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = expense.paymentFlowInformation

        return updateData
    }

    if (!userId && vendorId) return buildVendorRuleUpdateData(expense, vendorId, vendors, company)

    const vendorRules = buildVendorRuleUpdateData(expense, vendorId!, vendors, company)
    const employeeRules = buildEmployeeRuleUpdateData(userId!, users, companyId, expense.expenseAccount?._id)

    if (isEmptyObject(vendorRules) && isEmptyObject(employeeRules)) return updateData

    if (vendorRulesShouldOverwrite) {
        updateData.costCenter = vendorRules.costCenter ?? employeeRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = vendorRules.costCenter2 ?? employeeRules.costCenter2
        updateData.expenseAccount = vendorRules.expenseAccount ?? employeeRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = vendorRules.paymentFlowInformation ?? expense.paymentFlowInformation
    } else {
        updateData.costCenter = employeeRules.costCenter ?? vendorRules.costCenter ?? expense.costCenter
        updateData.costCenter2 = employeeRules.costCenter2 ?? vendorRules.costCenter2
        updateData.expenseAccount = employeeRules.expenseAccount ?? vendorRules.expenseAccount ?? expense.expenseAccount
        updateData.paymentFlowInformation = vendorRules.paymentFlowInformation ?? expense.paymentFlowInformation
    }
    return updateData
}

const buildVendorRuleUpdateData = (expense: Expense, vendorId: string, vendors: Array<Vendor>, company: any) => {
    const updateData: ExpenseUpdateData = { expenseAccount: undefined, paymentFlowInformation: undefined, costCenter: undefined, costCenter2: undefined }
    const vendor = vendors.find((vendorElement: Vendor) => vendorElement.id === vendorId)

    if (!vendor?.rule || (Object.keys(vendor?.rule).length === 1 && Object.keys(vendor?.rule)[0] === "_id")) return {}

    if (vendor.rule?.costCenter) updateData.costCenter = vendor?.rule?.costCenter ?? expense.costCenter
    if (vendor.rule?.costCenter2) updateData.costCenter2 = vendor?.rule?.costCenter2
    if (vendor.rule?.expenseAccount) updateData.expenseAccount = { _id: vendor?.rule?.expenseAccount } ?? expense.expenseAccount
    if (vendor.rule?.expensePaymentFlowInformation && expense.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD && !expense.isReimbursement) {
        if (!isPaymentFlowInfoDisabled(vendor.rule?.expensePaymentFlowInformation, company)) {
            updateData.paymentFlowInformation = vendor.rule?.expensePaymentFlowInformation
        }
    }

    return updateData
}

const buildEmployeeRuleUpdateData = (userId: string, users: Array<Employee>, companyId: string, expenseAccount?: string) => {
    const updateData: ExpenseUpdateData = { expenseAccount: expenseAccount ? { _id: expenseAccount } : undefined, costCenter: undefined, costCenter2: undefined }
    const user = users.find((userElement: User) => userElement.id === userId)
    const rule = user?.rule?.find((userRule: UserRule) => userRule.companyId === companyId)

    if (!rule) return {}

    if (rule?.costCenter) updateData.costCenter = rule?.costCenter
    if (rule?.costCenter2) updateData.costCenter2 = rule?.costCenter2
    if (rule?.expenseAccount) updateData.expenseAccount = { _id: rule?.expenseAccount }
    return updateData
}

export const getCostCenterSplitInfo = (costCenterSplits: Array<ExpenseSplit>, expense: Expense, loggedInProfile: Employee, taxes: Array<Tax>) =>
    costCenterSplits
        .map((s) => {
            const percentage = isInvoiceSplit(s, expense) ? s.percentage : 0
            let price = 0
            if (loggedInProfile.settings.showGrossAmount) {
                price = s.grossPrice
            } else {
                price = s.netPrice
            }

            return {
                percentage,
                price,
            }
        })
        .reduce(
            (total, curr) => ({
                percentage: total.percentage + curr.percentage,
                price: total.price + curr.price,
            }),
            { percentage: 0, price: 0 },
        )

export const dateSort = { field: "datePurchased", order: "desc" }

export const getDashboardSearchTargets = (useGross: boolean) => [
    "approverName",
    "requesterName",
    "vendorName",
    "invoiceNumber",
    "description",
    "items.name",
    "expenseNumber",
    useGross ? "totalGrossPrice" : "totalNetPrice",
]

export const isFullyProcessedSubscription = (expense: Expense) =>
    expense.kind === ExpenseKindEnum.SUBSCRIPTION &&
    expense.subscriptionType === SubscriptionTypeEnum.INITIAL_REQUEST &&
    [ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status)

export const isExpenseArchived = (expense: Expense, archiveAfterXDays: number) =>
    !isNotSet(expense.isManuallyArchived)
        ? expense.isManuallyArchived
        : [ExpenseStatusEnum.DONE, ExpenseStatusEnum.DECLINED].includes(expense.status) &&
          moment(expense.datePurchased).isSameOrBefore(moment().subtract(archiveAfterXDays, "days").toDate())

export const isExpenseManuallyUnarchived = (expense: Expense, archiveAfterXDays: number) =>
    !isNotSet(expense.isManuallyArchived) && !expense.isManuallyArchived && moment(expense.datePurchased).isSameOrBefore(moment().subtract(archiveAfterXDays, "days").toDate())

export const isSplitPropertiesSame = (splits: Array<ExpenseSplit>) => ({
    costCenter: splits.every((s) => s.costCenter === splits[0].costCenter),
    costCenter2: splits.every((s) => s.costCenter2 === splits[0].costCenter2),
    expenseAccount: splits.every((s) => s.expenseAccount?._id === splits[0].expenseAccount?._id),
    taxRate: splits.every((s) => s.taxRate?._id === splits[0].taxRate?._id),
})

export const calculateSplitPercentageTotal = (splits: Array<InvoiceSplit>) => splits.reduce((accu: number, split: InvoiceSplit) => accu + Number(split.percentage), 0)

/**
 * Type guard to determine if the splits of an expense are InvoiceSplit. If true, typescript can safely assume in the rest of the code block that the split is InvoiceSplit
 */
export const areInvoiceSplits = (splits: Array<ExpenseSplit>, expense: Expense): splits is Array<InvoiceSplit> => expense.splitType === SplitTypeEnum.SPLIT
export const isInvoiceSplit = (split: ExpenseSplit, expense: Expense): split is InvoiceSplit => expense.splitType === SplitTypeEnum.SPLIT

/**
 * Type guard to determine if the splits of an expense are ItemSplit. If true, typescript can safely assume in the rest of the code block that the split is ItemSplit
 */
export const areItemSplits = (splits: Array<ExpenseSplit>, expense: Expense): splits is Array<ItemSplit> => expense.splitType === SplitTypeEnum.ITEM
export const isItemSplit = (split: ExpenseSplit, expense: Expense): split is ItemSplit => expense.splitType === SplitTypeEnum.ITEM

export const isHospitalityExpense = (expense: Expense): expense is Hospitality => expense.kind === ExpenseKindEnum.HOSPITALITY
export const isPerDiemExpense = (expense: Expense): expense is PerDiem => expense.kind === ExpenseKindEnum.PER_DIEM
export const isMileageExpense = (expense: Expense): expense is Mileage => expense.kind === ExpenseKindEnum.MILEAGE
export const isMileageOrPerDiem = (expense: Expense): expense is Mileage | PerDiem => [ExpenseKindEnum.MILEAGE, ExpenseKindEnum.PER_DIEM].includes(expense.kind)
export const isFolderExpense = (expense: Expense): expense is TripFolder => expense.kind === ExpenseKindEnum.TRIP_FOLDER

export const getExpenseModel = (expense: Expense) => {
    const ExpenseModel = getExpenseModelByKind(expense.kind)
    return new ExpenseModel(expense)
}

export const getExpenseModelByKind = (kind: ExpenseKindEnum) => {
    switch (kind) {
        case ExpenseKindEnum.HOSPITALITY:
            return Hospitality
        case ExpenseKindEnum.PER_DIEM:
            return PerDiem
        case ExpenseKindEnum.MILEAGE:
            return Mileage
        case ExpenseKindEnum.TRIP_FOLDER:
            return TripFolder
        case ExpenseKindEnum.ONE_TIME_EXPENSE:
        case ExpenseKindEnum.SUBSCRIPTION:
        case ExpenseKindEnum.RECURRING_SUBSCRIPTION:
        default:
            return Expense
    }
}

export const isExpenseAlreadyPaid = (expense: Expense) => !isNotSet(expense.paymentFlowInformation) || [ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status)

/**
 * Mutates splits array so all the values of the selected property is changed to the new value
 */
export const changeAllSplitPropertyValues = (splits: Array<ExpenseSplit>, propertyToChange: "costCenter" | "costCenter2" | "expenseAccount", newValue: string) => {
    for (const split of splits) {
        if (propertyToChange === "expenseAccount") split[propertyToChange] = { _id: newValue }
        else split[propertyToChange] = newValue
    }
}

/**
 * @returns true if the expense could move past the APPROVED stage until the end.
 */
export const isExpenseAbleToProceed = (data: Expense) => {
    // Invoice data must be complete
    const isInvoiceDataComplete = Boolean(data.invoiceNumber && data.invoiceDate)

    // Cost center must be completely populated
    const isCostCenterComplete = data.splits?.length === 0 || data.splits?.every((s) => s.costCenter)

    // Creditor number must exist on APPROVED stage
    const isCreditorNumberOk = isExpenseCreditorNumberInfoOk(data)

    const itHasInvoicesIfInPaidStatus = expenseHasInvoiceInPaidStatus(data)

    return isInvoiceDataComplete && isCostCenterComplete && isCreditorNumberOk && itHasInvoicesIfInPaidStatus
}

export const isExpenseCreditorNumberInfoOk = (expense: Pick<Expense, "status" | "getCreditor">) =>
    expense.status <= ExpenseStatusEnum.APPROVED && Boolean(expense.getCreditor()?.creditorNumber)

export const expenseHasInvoiceInPaidStatus = (expense: Expense) => expense.status !== ExpenseStatusEnum.PAID || expense.invoices.length > 0

export const isExpenseSwitchingShown = (expense: Expense, archiveAfterXDays: number) => !isExpenseArchived(expense, archiveAfterXDays)

export const getTableAndAdjacentExpenseLabel = (status: ExpenseStatusEnum, requestedBy: string): [TablesEnum, string] => {
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const isRequestedByLoggedInUser = loggedInUser.id == requestedBy

    const activeTab = useActiveTab()

    if (activeTab === TabTypeEnum.ALL) {
        return [TablesEnum.ALL_REQUESTS, "all"]
    }

    switch (status) {
        case ExpenseStatusEnum.PURCHASE_PENDING:
            return isRequestedByLoggedInUser ? [TablesEnum.TODO_APPROVAL_PENDING, "approval_pending"] : [TablesEnum.TODO_PURCHASE_APPROVAL, "purchase_approval_needed"]
        case ExpenseStatusEnum.DOCS_NEEDED:
            return [TablesEnum.TODO_DOCS_NEEDED, "docs_needed"]
        case ExpenseStatusEnum.INVOICE_PENDING:
            return isRequestedByLoggedInUser ? [TablesEnum.TODO_APPROVAL_PENDING, "approval_pending"] : [TablesEnum.TODO_INVOICE_APPROVAL, "invoice_approval_needed"]
        case ExpenseStatusEnum.APPROVED:
            return AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__REVIEW) ? [TablesEnum.REVIEW_REQUESTS, "in_review"] : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.REVIEWED:
            return AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__EXPORT)
                ? [TablesEnum.PAY_AND_EXPORT_TO_BE_PAID, "reviewed"]
                : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.PAID:
            return AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__EXPORT)
                ? [TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED, "paid"]
                : [TablesEnum.IN_PROGRESS, "in_progress"]
        case ExpenseStatusEnum.DONE:
        case ExpenseStatusEnum.DECLINED:
            return [TablesEnum.DONE_REQUESTS, "done"]
        default:
            return [TablesEnum.TODO_PURCHASE_APPROVAL, ""]
    }
}

export const shouldExpenseIncludeVendors = (expense: Expense) => !isMileageOrPerDiem(expense)

export const shouldExpenseIncludeEmployeesAsVendor = (expense: Expense) => expense.isReimbursement

export const determineDefaultVendorForReimbursement = (requestedBy: string, expenseKind: ExpenseKindEnum, creditors: Array<CreditorInterface>) => {
    if (![ExpenseKindEnum.PER_DIEM, ExpenseKindEnum.MILEAGE].includes(expenseKind)) return undefined

    return creditors.find((c) => c.type === VendorTypeEnum.USER && c.id === requestedBy)
}

export const isExpenseReviewDisabled = (expenseData: any) =>
    expenseData.status === ExpenseStatusEnum.APPROVED &&
    expenseData.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
    (!expenseData.invoiceNumber || !expenseData.invoiceDate || !expenseData.invoices.length)

export const findAutoApproverInSet = (employees: Array<Employee>) => employees.find((approver) => approver.email === FINWAY_ADMIN_EMAIL)

export const isPaymentFlowInfoDisabled = (paymentFlowInformation: ExpensePaymentFlowInformationEnum, company: Company) => {
    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID && !company.expensePaymentFlowInformation?.isAlreadyPaidEnabled) {
        // already paid disabled
        return true
    }

    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID && !company.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled) {
        // already approved & paid disabled
        return true
    }

    if (paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY && !company.expensePaymentFlowInformation?.isReportingOnlyEnabled) {
        // reporting only disabled
        return true
    }

    return false
}

export const isPaymentOngoing = (isInPaymentProcess: boolean, payment?: FinApiPayment) => payment && (payment.status === FinApiPaymentStatusEnum.PENDING || isInPaymentProcess)

export const doesExpenseHaveDatevIncompliantBuCodes = (expense: Expense, taxValues: Array<any>) => {
    const usedTaxValues = [expense.taxRate, expense.splits.map((split) => split?.taxRate)].map((taxRateId) => taxValues.find((tax) => tax._id === taxRateId))
    return usedTaxValues.some((taxValue) => taxValue?.buCode && !isBuCodeDATEVCompliant(taxValue?.buCode))
}

export const showPaymentFailedAlert = (status: ExpenseStatusEnum, payment?: FinApiPayment) =>
    payment &&
    [FinApiPaymentStatusEnum.DISCARDED, FinApiPaymentStatusEnum.NOT_SUCCESSFUL].includes(payment.status) &&
    ![ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(status)

export const generatePerDiemDestinationTree = (perDiemDestinations: Array<PerDiemDestination>): Array<PerDiemDestination> => {
    // add children
    const treeDestinations: Array<PerDiemDestination> = []
    for (const destination of perDiemDestinations) {
        if (destination.parent) continue // if have a parent, skip...

        if (destination.children && destination.children.length > 0) {
            const childrenDestinations: Array<PerDiemDestination> = perDiemDestinations.filter((resource) => resource.parent === destination._id)
            destination.children = childrenDestinations
        }
        treeDestinations.push(destination)
    }
    return treeDestinations
}

export const getDefaultInvoiceNumber = (kind: ExpenseKindEnum, nextExpenseNumber?: number) => {
    if (!nextExpenseNumber) return undefined
    switch (kind) {
        case ExpenseKindEnum.PER_DIEM:
            return `finway-pd-${moment().get("year")}-${nextExpenseNumber}`
        case ExpenseKindEnum.MILEAGE:
            return `finway-ml-${moment().get("year")}-${nextExpenseNumber}`
        default:
            return undefined
    }
}

export const getStatusToResetTo = (expense: Expense) => {
    // -2 for reviewed since we got to jump over the rejected status)
    const defaultNewStatus = expense.status - (expense.status === ExpenseStatusEnum.REVIEWED ? 2 : 1)

    switch (expense.paymentFlowInformation) {
        case ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID:
            return expense.status === ExpenseStatusEnum.DONE ? ExpenseStatusEnum.PAID : ExpenseStatusEnum.APPROVED
        case ExpensePaymentFlowInformationEnum.ALREADY_PAID:
            return expense.status === ExpenseStatusEnum.PAID ? ExpenseStatusEnum.APPROVED : defaultNewStatus
        case ExpensePaymentFlowInformationEnum.REPORTING_ONLY:
            return ExpenseStatusEnum.APPROVED
        default:
            return defaultNewStatus
    }
}

const statusWithCreditorNumberExpected = [ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID]

export const isCreditorNumberReferenceMissing = (expense: Expense) => {
    const expenseNeedsCreditorNumber = statusWithCreditorNumberExpected.includes(expense.status)
    const creditor = expense.getCreditor()
    const creditorNumberAvailable = creditor?.type === VendorTypeEnum.VENDOR || creditor?.creditorNumber
    const userCanSee = AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ)
    return expenseNeedsCreditorNumber && !creditorNumberAvailable && userCanSee
}

export const isCreditorNumberMissing = (expense: Expense, vendor: CreditorInterface) =>
    statusWithCreditorNumberExpected.includes(expense.status) &&
    vendor?.type === VendorTypeEnum.USER &&
    !vendor.creditorNumber &&
    AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ)

export const toDinero = (amount: number) => dinero({ amount: Math.round(amount * 100) })

export const toAmount = (dineroInstance: dinero.Dinero) => dineroInstance.getAmount() / 100

export const detectSplitRoundingError = (splits: Array<ExpenseSplit>) => {
    const expectedAmount = {
        net: 0,
        tax: 0,
        gross: 0,
    }
    const roundedAmount = {
        net: 0,
        tax: 0,
        gross: 0,
    }
    for (const split of splits) {
        expectedAmount.net += split.netPrice
        roundedAmount.net += roundNumberTo2Decimals(split.netPrice)

        expectedAmount.tax += split.taxPrice
        roundedAmount.tax += roundNumberTo2Decimals(split.taxPrice)

        expectedAmount.gross += split.grossPrice
        roundedAmount.gross += roundNumberTo2Decimals(split.grossPrice)
    }

    return {
        netPrice: roundNumberTo2Decimals(expectedAmount.net - roundedAmount.net),
        grossPrice: roundNumberTo2Decimals(expectedAmount.gross - roundedAmount.gross),
        taxPrice: roundNumberTo2Decimals(expectedAmount.gross - roundedAmount.gross),
    }
}

/**
 * Calculates if there is an error in the amounts vertically (net / gross / tax).
 * returns the difference between: (Split 0's price + ... + Split n's price) and (Expense price)
 * 0 in tax/gross/net means the amounts is fine.
 */
export const detectVerticalSplitError = (expense: Expense, splits: Array<ExpenseSplit>) => {
    const splitAmount = {
        net: toDinero(0),
        tax: toDinero(0),
        gross: toDinero(0),
    }

    for (const split of splits) {
        splitAmount.net = splitAmount.net.add(toDinero(split.netPrice))
        splitAmount.tax = splitAmount.tax.add(toDinero(split.taxPrice))
        splitAmount.gross = splitAmount.gross.add(toDinero(split.grossPrice))
    }

    splitAmount.net = splitAmount.net.subtract(toDinero(expense.totalNetPrice))
    splitAmount.tax = splitAmount.tax.subtract(toDinero(expense.totalTaxPrice))
    splitAmount.gross = splitAmount.gross.subtract(toDinero(expense.totalGrossPrice))

    return {
        netPrice: toAmount(splitAmount.net),
        taxPrice: toAmount(splitAmount.tax),
        grossPrice: toAmount(splitAmount.gross),
    }
}

/**
 * Calculates if there is an error horizontally
 * Returns array of indices of rows that doesnt add up
 * Split 0's net + tax should equal gross
 */
export const detectHorizontalSplitError = (splits: Array<ExpenseSplit>) => {
    const errors: Array<number> = []
    splits.forEach((split, index) => {
        const { netPrice, taxPrice, grossPrice } = split
        if (!toDinero(netPrice).add(toDinero(taxPrice)).equalsTo(toDinero(grossPrice))) errors.push(index)
    })

    return errors
}

export const convertAmountToInteger = (amt: number) => Math.round(amt * 100)

export const adjustGrossAmountsIfNeeded = (splits: Array<ExpenseSplit>, expense: Expense) => {
    if (!areInvoiceSplits(splits, expense)) {
        return splits
    }

    const roundingError = detectSplitRoundingError(splits)
    if (roundingError.grossPrice !== 0) {
        const totalGross = splits.reduce((acc, val) => acc + val.grossPrice, 0)
        const percentages = splits.map((s) => Number(s.percentage))
        const grossPriceAllocation = dinero({ amount: convertAmountToInteger(totalGross) }).allocate(percentages)
        return [...splits].map((s, i) => ({
            ...s,
            grossPrice: grossPriceAllocation[i].getAmount() / 100,
        }))
    }

    return splits
}

export const roundSplitAmounts = (splits: Array<ExpenseSplit>): Array<ExpenseSplit> =>
    splits.map((split) => ({
        ...split,
        netPrice: toAmount(toDinero(split.netPrice)),
        grossPrice: toAmount(toDinero(split.grossPrice)),
        taxPrice: toAmount(toDinero(split.taxPrice)),
    }))
export const isMatched = (expense: Expense) => summarizeExpenseTransactions(expense) !== undefined

export const addInvoiceCommentsIfNeeded = (expense: Expense, previousExpense: Expense, user: User) => {
    const newExpenseUrls = expense.invoices.map(({ url }: InvoiceInterface) => url)
    const previousExpenseUrls = previousExpense.invoices.map(({ url }: InvoiceInterface) => url)

    const newInvoices = expense.invoices.filter(({ url }: InvoiceInterface) => !previousExpenseUrls.includes(url))
    const removedInvoices = previousExpense.invoices.filter(({ url }: InvoiceInterface) => !newExpenseUrls.includes(url))

    const commentMessages = [
        ...removedInvoices.map(({ fileName }: InvoiceInterface) =>
            fileName ? `${AutoGeneratedCommentsEnum.INVOICE_DELETED_WITH_FILENAME},${fileName}` : AutoGeneratedCommentsEnum.INVOICE_DELETED,
        ),
        ...newInvoices.map(({ fileName }: InvoiceInterface) =>
            fileName ? `${AutoGeneratedCommentsEnum.INVOICE_UPLOADED_WITH_FILENAME},${fileName}` : AutoGeneratedCommentsEnum.INVOICE_UPLOADED,
        ),
    ]
    expense.comments.push(
        ...commentMessages.map(
            (message: string) =>
                new CommentItem({
                    postedBy: user.id,
                    message,
                    datePosted: new Date(),
                    isAutoGenerated: true,
                }),
        ),
    )
}

export const getReimbursementLabel = (kind: ExpenseKindEnum) => {
    switch (kind) {
        case ExpenseKindEnum.HOSPITALITY:
            return "hospitality"
        case ExpenseKindEnum.PER_DIEM:
            return "per_diem"
        case ExpenseKindEnum.MILEAGE:
            return "mileage"
        case ExpenseKindEnum.STANDARD:
            return "standard"
        default:
            return "standard_reimbursement"
    }
}

// A product requirement for export filter in ALL tab
export const getFilteredExpenseStatusArrayForExport = (exportFormat: ExportFormatEnum) => {
    switch (exportFormat) {
        case ExportFormatEnum.DATEV:
        case ExportFormatEnum.DATEV_INVOICE_CSV:
            return [ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE]
        case ExportFormatEnum.ALL:
        case ExportFormatEnum.CSV:
            return [
                ExpenseStatusEnum.PURCHASE_PENDING,
                ExpenseStatusEnum.DOCS_NEEDED,
                ExpenseStatusEnum.INVOICE_PENDING,
                ExpenseStatusEnum.APPROVED,
                ExpenseStatusEnum.REVIEWED,
                ExpenseStatusEnum.PAID,
                ExpenseStatusEnum.DONE,
                ExpenseStatusEnum.DECLINED,
            ]
        case ExportFormatEnum.INVOICES:
            return [ExpenseStatusEnum.INVOICE_PENDING, ExpenseStatusEnum.APPROVED, ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE]
        default:
            return []
    }
}

// if expense format is different than INVOICES or ALL (they don't advance status by default) and the expense status is or includes PAID then we let the user decide if they want to advance expense status to DONE
export const shouldAskToAdvanceStatusOnExport = (format: ExportFormatEnum, status: any) =>
    ![ExportFormatEnum.INVOICES, ExportFormatEnum.ALL].includes(format) && (!status || Number(status) === ExpenseStatusEnum.PAID)

export const addUpdateCommentToExpense = (expense: Expense, loggedInUser: User) => {
    expense.comments.unshift(
        new CommentItem({
            postedBy: loggedInUser.id,
            message: AutoGeneratedCommentsEnum.REQUEST_UPDATED,
            datePosted: new Date(),
            isAutoGenerated: true,
        }),
    )
}

export const getExpenseReminderForUser = (reminders: Array<ExpenseReminder>, userId: string) => reminders?.find((reminder) => reminder.user === userId)

/**
 * Will automatically filter for folders, and selects the childrens instead
 * e.g.
 * we are exporting the following:
 * Expense A, B, C, and Folder D (children: Expense X, Y)
 * This will return A,B,C,X,Y
 * if includeFolder is set to true, it will return A,B,C,D,X,Y
 *
 * folders passed through expenses require .children to be populated
 */
export const getExpensesToPayOrExportForTables = (expenses: Array<Expense>, currentExpectedStatus?: ExpenseStatusEnum, includeFolder = false) => {
    const actionableExpenses: Array<Expense> = []

    for (const expense of expenses) {
        if (actionableExpenses.includes(expense) || (currentExpectedStatus && expense.status !== currentExpectedStatus)) continue

        if (isFolderExpense(expense)) {
            actionableExpenses.push(...getExpensesToPayOrExportForTables(expense.children, currentExpectedStatus))
            if (!includeFolder) {
                continue
            }
        }

        actionableExpenses.push(expense)
    }

    return getUniqueElements(actionableExpenses, "id")
}

export const isExpenseReviewable = (expense: Expense) =>
    (expense.status === ExpenseStatusEnum.APPROVED && isExpenseAbleToProceed(expense)) || expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY

export const isReviewButtonEnabled = (activeExpense: Expense, subExpenses: Array<Expense>) => {
    if (isFolderExpense(activeExpense)) {
        if (subExpenses.length === 0) return false
        const { processableSubExpenses, canAdvance } = getFolderProgressionInfo(activeExpense, subExpenses)
        return canAdvance && processableSubExpenses.every((subExpense) => isExpenseReviewable(subExpense))
    }
    return isExpenseReviewable(activeExpense)
}

export const sumTotalGrossPriceByCurrencies = (expenses: Array<Expense>) => {
    const totalGrossPriceByCurrency = new Map<CurrencyEnum, number>()

    for (const expense of expenses) {
        const currentTotalPrice = totalGrossPriceByCurrency.get(expense.currency)
        const discount = expense.getActiveDiscount()
        const grossPriceToPay = discount && !isDiscountExpired(expense) ? discount.reducedAmount.gross : expense.totalGrossPrice

        if (currentTotalPrice) {
            totalGrossPriceByCurrency.set(expense.currency, toAmount(toDinero(currentTotalPrice).add(toDinero(grossPriceToPay))))
        } else {
            totalGrossPriceByCurrency.set(expense.currency, toAmount(toDinero(grossPriceToPay)))
        }
    }

    return totalGrossPriceByCurrency
}

export const shouldDisplayUserAsTagged = (isMyExpense: boolean, isApproverOfAnyApprovalProcess: boolean, loggedInProfile: Employee, expense: Expense) => {
    if (!expense.userTagging) return false
    const taggedUsers = expense.userTagging?.map((tagging) => tagging.tagged)
    return !isMyExpense && !isApproverOfAnyApprovalProcess && !AuthzService.isLoggedInUserAllowedToApproveExpense(expense) && taggedUsers?.includes(loggedInProfile.id)
}

export const exportSepa = async (expenses: Array<Expense>, subjectLines: any, t: any) => {
    try {
        const response = await FileService.export(`&_id[in]=${expenses.map((exp) => exp.id).join(",")}&format=${ExportFormatEnum.SEPA}`, false, subjectLines)
        const isAsyncExport = response?.status === httpStatus.ACCEPTED

        if (isAsyncExport) {
            const filteredOutExpenseNumbers = response?.data.filteredOutExpenseNumbers
            if (filteredOutExpenseNumbers?.length) {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:request.async_export_started_partial.title`),
                    i18n.t(`notification:request.async_export_started_partial.message`, { expenses: filteredOutExpenseNumbers.join(", ") }),
                    0,
                )
            } else {
                NotificationService.send(NotificationTypeEnum.INFO, i18n.t(`notification:async_export_started.title`), i18n.t(`notification:async_export_started.message`), 0)
            }
        } else {
            if (response.data.filteredOutExpenseNumbers?.length) {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:request.exported_bulk_partial.title`),
                    i18n.t(`notification:request.exported_bulk_partial.message`, { expenses: response.data.filteredOutExpenseNumbers.join(", ") }),
                    0,
                )
            }
            FileService.downloadFileAsSignedUrl(response?.data.fileUrl)
        }
    } catch (error) {
        NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:file:export:title"))
    }
}

export const isExpenseInformationCompleteForReview = (expense: Expense) => {
    const isNotAbleToProceed = !isExpenseAbleToProceed(expense) && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY

    const isInReview = expense.status === ExpenseStatusEnum.APPROVED

    return isInReview && !isNotAbleToProceed
}

export const isExpenseCreditorApproved = (expense: Expense, creditorMap: Map<string, CreditorInterface>) => {
    const creditorRef = expense.getCreditor()
    if (!creditorRef) return false
    if (creditorRef?.type == VendorTypeEnum.USER) return true

    // Extended reference of vendor in expenses does not store isApproved, so we still have to fetch the whole creditor object.
    const creditor = creditorMap.get(creditorRef._id)
    return (creditor?.source as Vendor)?.isApproved
}

/**
 * Passing an folder in the expense param needs .children to be populated
 */
export const diagnoseExpenseForReview = (expense: Expense, creditorMap: Map<string, CreditorInterface>) => {
    // Is all the expense data OK and the expense is in the correct state?
    let isReviewable = isExpenseInformationCompleteForReview(expense)

    // Is the vendor attached to the expense already approved?
    let isVendorApproved = isExpenseCreditorApproved(expense, creditorMap)

    // If it's a folder expense, check if all the children are ready to review
    if (isFolderExpense(expense)) {
        const { processableSubExpenses, canAdvance } = getFolderProgressionInfo(expense, expense.children) // checks by expense.children for tables
        isReviewable = expense.children.length > 0 ? processableSubExpenses.every((child: Expense) => isExpenseInformationCompleteForReview(child)) && canAdvance : false
        isVendorApproved = processableSubExpenses.every((child: Expense) => isExpenseCreditorApproved(child, creditorMap))
    }

    // Return the diagnostic result. Values are separated for the caller to be able to show the correct message
    return {
        isReviewable,
        isVendorApproved,
    }
}

export const isExpenseEditableBasedOnArchive = (isArchived: boolean) => !isArchived || !CompanyService.doesCompanyEnforceGobdCompliantInvoice()

export const isExpenseExportable = (expense: Expense, loggedInUser: Employee) => {
    const invoiceWillBeAutoGenerated = [ExpenseKindEnum.PER_DIEM, ExpenseKindEnum.MILEAGE].includes(expense.kind)
    const invoiceIsAttached = expense.invoices?.length > 0
    const isMyExpense = expense.requestedBy?._id === loggedInUser.id
    const isApproverOfAnyApprovalProcess = getApproverIdsOfApprovalProcesses(expense.approvalProcesses).includes(loggedInUser.id)
    const isExpenseRelevantToMe = isMyExpense || isApproverOfAnyApprovalProcess

    return (AuthzService.isLoggedInUserGlobalApprover() || isExpenseRelevantToMe) && (invoiceIsAttached || invoiceWillBeAutoGenerated)
}

export const isEditButtonVisible = (expense: Expense, loggedInUser: Employee, archiveAfterXDays: number) => {
    const isMyExpense = expense.requestedBy?._id === loggedInUser.id
    const isApproverOfAnyApprovalProcess = getApproverIdsOfApprovalProcesses(expense.approvalProcesses).includes(loggedInUser.id)
    const isExpenseRelevantToMe = isMyExpense || isApproverOfAnyApprovalProcess || AuthzService.isLoggedInUserAllowedToApproveExpense(expense)
    const isArchived = isExpenseArchived(expense, archiveAfterXDays)

    // expense.isReadOnly is pre-calculated in the backend's getOne expense endpoint to calculate the user's permission to edit the expense. If this is not provided, calculate by ourself.
    const isReadOnly = expense.isReadOnly ?? !isExpenseRelevantToMe

    return !isReadOnly && isExpenseEditableBasedOnArchive(isArchived) && !expense.deleted
}

export const isEditButtonDisabled = (expense: Expense, t: TFunction) => {
    const isNotEditableBecauseOfStatus = (expense.status === ExpenseStatusEnum.PAID && !AuthzService.isLoggedInUserGlobalApprover()) || expense.status === ExpenseStatusEnum.DONE
    const isReportingOnly = expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY

    if (isNotEditableBecauseOfStatus && !isReportingOnly) {
        return {
            disabled: true,
            reason: t("tooltips:expenses.not_editable_exported"),
        }
    }

    if (expense.deleted) {
        return {
            disabled: true,
        }
    }

    if (expense.isGobdCompliantInvoice()) {
        return {
            disabled: true,
            reason: t("tooltips:expenses.edit_disabled_due_to_beleg"),
        }
    }

    return {
        disabled: false,
    }
}

export const calculateExpenseEditButtonView = (
    expense: Expense,
    loggedInUser: Employee,
    archiveAfterXDays: number,
    t: TFunction,
): { buttonVisible: boolean; buttonDisabled: boolean; reasonToDisplay?: string; expenseEditable: boolean } => {
    const { disabled, reason } = isEditButtonDisabled(expense, t)
    const visible = isEditButtonVisible(expense, loggedInUser, archiveAfterXDays)

    return {
        buttonVisible: visible,
        expenseEditable: visible && !disabled,
        buttonDisabled: disabled,
        reasonToDisplay: reason,
    }
}

/**
 * Billable cannot be manipulated by user of th expense is in the purchase pending states and when it's a subscription.
 */
export const isExpenseBillableAccessible = (expense: Expense) =>
    ![ExpenseStatusEnum.PURCHASE_PENDING, ExpenseStatusEnum.DOCS_NEEDED].includes(expense.status) && !expense.isSubscription()

/**
 * Checks if invoice in the expense is provided. For mileage/perdiem, invoice is automatically generated internally
 */
export const isExpenseInvoiceProvided = (expense: Expense) => isMileageOrPerDiem(expense) || expense.invoices?.length > 0

export const onlyAllowFileExports = (expense: Expense) =>
    ![ExpenseStatusEnum.REVIEWED, ExpenseStatusEnum.PAID, ExpenseStatusEnum.DONE].includes(expense.status) ||
    !AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__EXPORT) ||
    expense.deleted

export const isExpenseVendorDataOutdated = (vendor: Vendor | (Employee & { creditorNumber?: string }), vendorData?: ExtendedReferencedVendor | ExtendedReferencedUser): boolean => {
    if (!vendorData) return false

    if (vendor instanceof Employee) vendor.creditorNumber = vendor.activeCompanyProfile.creditorNumber

    const vendorKeys = Object.keys(vendor)
    const vendorValues = Object.values(vendor)
    const vendorDataEnumerable = Object.entries(vendorData)
    return vendorDataEnumerable.filter(([key, value]) => vendorKeys.includes(key) && vendorValues.includes(value)).length !== vendorDataEnumerable.length
}

export const adjustFilterDataForSelectedExpenseType = (values: Store) => {
    // Standard expense type filter translates to kind === ONE_TIME_EXPENSE and isReimbursement === true
    // we don't have in the database expenses with STANDARD kind, we parse the kind
    if (values.kind === ExpenseKindEnum.STANDARD) return parseStandardReimbursementExpenseFormValues(values)
    // One time expense type filter translates to kind === ONE_TIME_EXPENSE and isReimbursement === false
    if (values.kind === ExpenseKindEnum.ONE_TIME_EXPENSE) return parseOneTimeExpenseExpenseFormValues(values)

    return values
}

export const parseStandardReimbursementExpenseFormValues = (values: Store) => ({
    ...values,
    kind: ExpenseKindEnum.ONE_TIME_EXPENSE,
    isReimbursement: true,
})

export const parseOneTimeExpenseExpenseFormValues = (values: Store) => ({
    ...values,
    kind: ExpenseKindEnum.ONE_TIME_EXPENSE,
    isReimbursement: false,
})

export const isExpenseMissingCostCenter1 = (expense: Expense): boolean => !(expense.costCenter || expense.splits.some((split: ExpenseSplit) => split.costCenter))

export const getCostCentersFromSplits = (expenseSplits: Array<ExpenseSplit>): Array<string> => [
    ...new Set(expenseSplits.filter((expenseSplit: ExpenseSplit) => expenseSplit.costCenter).map((expenseSplit) => expenseSplit.costCenter) as Array<string>),
]

export const getErrorMessageForDuoExportFailure = (failureType?: string) =>
    i18n.exists(`info:datev_export_job.failure_type_enum.${failureType}`)
        ? i18n.t(`info:datev_export_job.failure_type_enum.${failureType}`)
        : i18n.t(`info:datev_export_job.failure_type_enum.UNKNOWN`)

export const isAtLeastReviewed = (status: ExpenseStatusEnum): boolean =>
    status === ExpenseStatusEnum.REVIEWED || status === ExpenseStatusEnum.PAID || status === ExpenseStatusEnum.DONE

/**
 * To properly get the expenses to be exported, when incoming expense is a folder.
 * E.g. When the expense param is folder A with A.children = [X,Y,Z], this will return [X,Y,Z]. It filters out expenses that are not in the suitable status for exports.
 * When the expense param is folder J, it will return [J]
 *
 * @param expense expense that we want to export. Expense should be expense on the table so that expense.children is populated
 * @param includeFolder indicates when the expense to be exported is a folder, should the folder itself be included or not (following the case above, when this flag is true it will return [A,X,Y,Z])
 * @returns array of expense that can be exported
 */
export const normalizeExpenseToExport = (expense: Expense, includeFolder?: boolean): Array<Expense> => {
    if (!isFolderExpense(expense)) {
        return [expense]
    }

    const expensesToExport: Array<Expense> = (expense.children ?? []).filter((subExpense) => isAtLeastReviewed(subExpense.status))
    if (includeFolder) expensesToExport.push(expense)
    return expensesToExport
}

export const normalizeExpensesToExport = (expenses: Array<Expense>, includeFolder?: boolean): Array<Expense> => {
    const normalizedExpenses = expenses.flatMap((expense) => normalizeExpenseToExport(expense, includeFolder))
    // normalizedExpenses might contain duplicate, because some folder's children might be already passed in expenses.
    const uniqueIds = [...new Set(normalizedExpenses.map((expense) => expense.id))]
    return uniqueIds.map((id) => normalizedExpenses.find((expense) => expense.id === id) as Expense)
}

export const createLinkToRequestFunction = (expense: Expense, history: RouterChildContext["router"]["history"]) => {
    const isDoneParentSubscription = expense.isInitialSubscription() && expense.isSuccessfullyProcessed()
    let url = `/expenses/${expense.id}`

    if (isDoneParentSubscription) {
        url = `/subscriptions/${expense.id}`
    }

    return (_event: React.KeyboardEvent | React.MouseEvent) => {
        history.push(url)
    }
}

export const determineExpensePrice = (taxRate: number, price: number, priceSource: PriceSourceEnum) => {
    switch (priceSource) {
        case PriceSourceEnum.NET_PRICE: {
            const taxPrice = toDinero(price).percentage(taxRate)
            return {
                netPrice: price,
                taxPrice: toAmount(taxPrice),
                grossPrice: toAmount(toDinero(price).add(taxPrice)),
            }
        }
        case PriceSourceEnum.GROSS_PRICE:
        default: {
            const netPrice = calculateNetPriceFromGrossPriceAndTaxRate(price, taxRate)
            return {
                grossPrice: price,
                netPrice,
                taxPrice: toAmount(toDinero(price).subtract(toDinero(netPrice))),
            }
        }
    }
}
